import { Eye, Handshake, Scale, ShieldCheck } from "lucide-react";
import { AboutManifesto } from "@/components/AboutManifesto";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { Reveal } from "@/components/Reveal";

const ROOTS = [
  { title: "Generations in the trade", body: "Sunrays is founded by a family whose hands have graded, cut, and traded diamonds for generations — long before the first certificate went digital." },
  { title: "Schooled at the bourses", body: "Decades of combined desk experience across the world's great diamond markets, from cutting floors to bourse trading halls." },
  { title: "New to Dubai", body: "2026 marks our first Dubai desk — bringing an old-trade network and old-trade ethics to the fastest-growing diamond hub on earth." },
];

const VALUES = [
  { icon: ShieldCheck, title: "Provenance", body: "Every stone screened and documented before it is ever offered." },
  { icon: Eye, title: "Transparency", body: "Indicative pricing shown openly on our Market Explorer — no games." },
  { icon: Scale, title: "Precision", body: "Cut, make, fluorescence and price position checked against the market." },
  { icon: Handshake, title: "Partnership", body: "Memo terms, call-off stock and standing briefs for repeat houses." },
];

const STATS = [
  { value: "400+", label: "Active trade accounts" },
  { value: "12k+", label: "Certified stones traded" },
  { value: "4", label: "Global partner desks" },
  { value: "48h", label: "Bespoke sourcing turnaround" },
];

export const AboutPage = () => (
  <div className="pt-24" data-testid="about-page">
    <header className="mx-auto max-w-7xl px-6 pb-8 pt-16 lg:px-12">
      <Reveal>
        <p className="eyebrow mb-4">About Sunrays</p>
        <h1 className="font-display max-w-3xl text-4xl font-light leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          The house behind <span className="gold-text italic">the stone</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8BCC2] sm:text-lg">
          Sunrays is a Dubai loose-diamond trading house serving jewelers, retailers, and wholesale
          buyers who need certified goods, honest positioning, and a desk that answers.
        </p>
      </Reveal>
    </header>

    <AboutManifesto />
    <EditorialMarquee />

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12" data-testid="about-roots">
      <Reveal>
        <p className="eyebrow mb-4">Our roots</p>
        <h2 className="font-display max-w-3xl text-3xl font-light leading-snug text-white sm:text-4xl lg:text-5xl">
          New to Dubai. <span className="gold-text italic">Generations deep in diamonds.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8BCC2]">
          Sunrays launches in JLT carrying something no new desk can buy — inherited judgment. The kind
          built over generations of buying, cutting, and trading stones across the world's diamond markets,
          now planted in the city where the trade's future is being written.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {ROOTS.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.08}>
            <div className="glass-panel h-full rounded-2xl p-8 transition-all duration-500 hover:border-[#C9A24B]/40" data-testid={`roots-${i}`}>
              <span className="font-display text-sm italic tracking-[0.3em] text-[#C9A24B]">0{i + 1}</span>
              <h3 className="font-display mt-3 text-2xl font-light text-white">{r.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#B8BCC2]">{r.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-12" data-testid="about-values">
      <Reveal>
        <p className="eyebrow mb-4">What we trade by</p>
      </Reveal>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08}>
            <div className="glass-panel h-full rounded-2xl p-8 transition-all duration-500 hover:border-[#C9A24B]/40" data-testid={`value-${v.title.toLowerCase()}`}>
              <v.icon className="mb-4 h-5 w-5 text-[#C9A24B]" strokeWidth={1.5} />
              <h3 className="font-display text-xl text-white">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#B8BCC2]">{v.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-16 grid grid-cols-2 gap-8 lg:grid-cols-4" data-testid="about-stats">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-4xl font-light gold-text lg:text-5xl">{s.value}</p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#717680]">{s.label}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  </div>
);
